import { useCallback, useEffect, useState } from "react";
import {
  ClassError,
  closeClass,
  createClass,
  fetchUnplaced,
  placeStudent,
  renameClass,
  type Unplaced,
} from "../class-api.js";
import { COPY, fill } from "../copy.js";
import { fa } from "../format.js";
import { useClasses } from "./class-context.js";
import { ScreenHeader } from "./screen-header.js";

/**
 * The classes themselves: which one is open, and adding, renaming and closing them.
 *
 * **Its own screen rather than a menu on the roster.** The first version hung all of this off the
 * class name in the roster header, which meant a teacher managing their groups was doing it inside a
 * panel that kept re-reading a roster underneath — and every action collapsed the menu they were
 * working in. Four verbs deserve a page.
 *
 * What stays on the roster is the one thing that belongs there: which class is open, as a heading.
 */

const CARD = "rounded-lg bg-card shadow-card";
const FIELD =
  "h-[52px] w-full rounded-md border-2 border-edge bg-card px-md text-body outline-none transition-colors focus-visible:border-grape";
const PRIMARY =
  "min-h-touch w-full rounded-pill bg-grape px-lg py-md text-heading text-card shadow-tint-grape disabled:opacity-60";
const QUIET = "block w-full py-sm text-center text-small text-muted";

type Editing =
  | { kind: "none" }
  | { kind: "new" }
  | { kind: "rename"; classId: string }
  | { kind: "close"; classId: string };

export function ClassesScreen({ onBack }: { onBack: () => void }) {
  const { classes, current, allowance, choose, reload } = useClasses();
  const [editing, setEditing] = useState<Editing>({ kind: "none" });
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unplaced, setUnplaced] = useState<Unplaced>([]);

  /**
   * Re-read after anything that could change it.
   *
   * Closing a class creates unplaced children and placing one removes them from this list, so the two
   * lists on this screen are two views of the same fact and must not drift apart while it is open.
   */
  const loadUnplaced = useCallback(async () => {
    try {
      setUnplaced(await fetchUnplaced());
    } catch {
      // A failure here leaves the classes above usable. This section simply does not appear, which
      // is the same thing it does when there is nobody waiting.
      setUnplaced([]);
    }
  }, []);

  useEffect(() => {
    void loadUnplaced();
  }, [loadUnplaced]);

  const open = (next: Editing, withName = "") => {
    setName(withName);
    setError(null);
    setEditing(next);
  };

  /** Runs a write and keeps the form open on refusal, so the message has somewhere to be. */
  const act = async (run: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await run();
      await Promise.all([reload(), loadUnplaced()]);
      setEditing({ kind: "none" });
    } catch (err) {
      const refusal = err instanceof ClassError ? err : null;
      setError(
        refusal?.failure === "class-name-taken"
          ? COPY.teacher.classes.nameTaken
          : refusal?.failure === "last-class"
            ? COPY.teacher.classes.lastClass
            : /*
               * Reachable even though the button that opens this form is hidden when the allowance is
               * full: the count this screen holds can be a few seconds old, and another device signed
               * into the same account can have used the last place. The limit in the sentence comes from
               * the refusal rather than from what is on screen, so it is the number that actually applied.
               */
              refusal?.failure === "class-limit"
              ? fill(COPY.teacher.classes.limitFull, { limit: fa(refusal.limit ?? 0) })
              : COPY.enter.failed,
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * Whether another class is on offer.
   *
   * Unknown counts as yes. The server is the rule and refuses regardless; hiding the button on a guess
   * would leave a teacher with no way to find out why, which is the worse of the two failures.
   */
  const roomForClass = !allowance || allowance.classes < allowance.classLimit;

  return (
    <div className="mx-auto min-h-full max-w-[560px] px-lg pt-lg pb-xxl">
      <ScreenHeader
        title={COPY.teacher.classes.title}
        subtitle={COPY.teacher.classes.intro}
        onBack={onBack}
        backLabel={COPY.teacher.classes.back}
      />

      <ul className={`${CARD} mb-lg overflow-hidden`}>
        {classes.map((c) => {
          const active = c.classId === current?.classId;
          return (
            <li key={c.classId} className="border-b border-edge last:border-b-0">
              <div className="flex items-center gap-sm px-md py-sm">
                {/*
                  * Choosing is the whole row, so the common action is the easy one.
                  *
                  * The tick is the state; there is no separate "make active" button, because a row
                  * that is tapped and does nothing is worse than a row that does the obvious thing.
                  */}
                <button
                  onClick={() => choose(c.classId)}
                  className="flex min-w-0 flex-1 items-center gap-sm py-sm text-start"
                  aria-pressed={active}
                >
                  <span
                    className={`grid size-6 shrink-0 place-items-center rounded-pill ${
                      active ? "bg-grape text-card" : "border-2 border-edge"
                    }`}
                  >
                    {active && <Tick />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body">{c.name}</span>
                    <span className="block text-caption text-muted">
                      {fill(COPY.teacher.classes.count, { count: fa(c.students) })}
                    </span>
                  </span>
                </button>

                <button
                  onClick={() => open({ kind: "rename", classId: c.classId }, c.name)}
                  className="shrink-0 rounded-pill px-sm py-1 text-caption text-muted"
                >
                  {COPY.teacher.classes.rename}
                </button>
                {/*
                  * Closing is offered only when another class remains.
                  *
                  * The server refuses the last one regardless, and that is the check that matters —
                  * this is so a teacher is not offered a button whose only outcome is a refusal.
                  */}
                {classes.length > 1 && (
                  <button
                    onClick={() => open({ kind: "close", classId: c.classId })}
                    className="shrink-0 rounded-pill px-sm py-1 text-caption text-watermelon"
                  >
                    {COPY.teacher.classes.close}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/*
        * The offer, or the reason there is none.
        *
        * The same shape the close button follows on a teacher's last class: rather than leave a control
        * whose only outcome is a refusal, the button is replaced by the sentence that explains it — and
        * that sentence names the number and the two ways out, one of which is theirs.
        */}
      {editing.kind === "none" &&
        (roomForClass ? (
          <button onClick={() => open({ kind: "new" })} className={PRIMARY}>
            {COPY.teacher.classes.add}
          </button>
        ) : (
          <p className={`${CARD} px-md py-md text-small text-muted`}>
            {fill(COPY.teacher.classes.limitFull, { limit: fa(allowance?.classLimit ?? 0) })}
          </p>
        ))}

      {/*
        * Children left behind by a closed class.
        *
        * Shown only when there are any, and shown here rather than on the roster: they are between
        * classes, and putting them in a roster would say they are somewhere they are not. Nothing of
        * theirs was lost — closing is soft — so the section reads as a queue rather than as damage.
        */}
      {unplaced.length > 0 && (
        <section className={`${CARD} mt-lg overflow-hidden`}>
          <div className="px-md pt-md">
            <h2 className="text-title">{COPY.teacher.classes.unplacedTitle}</h2>
            <p className="mt-1 text-caption text-muted">{COPY.teacher.classes.unplacedNote}</p>
          </div>
          <ul className="mt-sm">
            {unplaced.map((student) => (
              <li
                key={student.studentId}
                className="flex flex-wrap items-center gap-sm border-t border-edge px-md py-sm"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-body">
                    {[student.firstName, student.lastName].filter(Boolean).join(" ")}
                  </span>
                  <span className="block text-caption text-muted">
                    {fill(COPY.teacher.classes.unplacedFrom, { name: student.formerClassName })}
                  </span>
                </span>
                {/*
                  * Into the ACTIVE class, named on the button.
                  *
                  * A dropdown of every class per row would be the general answer and the wrong one: a
                  * teacher rescuing a cohort is putting them all in the same place, and the class
                  * picker for that is the list directly above.
                  */}
                {current && (
                  <button
                    disabled={busy}
                    onClick={() =>
                      void act(async () => {
                        await placeStudent(current.classId, student.studentId);
                      })
                    }
                    className="shrink-0 rounded-pill bg-grape px-md py-1 text-caption text-card disabled:opacity-60"
                  >
                    {fill(COPY.teacher.classes.placeHere, { name: current.name })}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {(editing.kind === "new" || editing.kind === "rename") && (
        <form
          className={`${CARD} flex flex-col gap-md px-md py-md`}
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = name.trim();
            if (!trimmed || busy) return;
            void act(async () => {
              if (editing.kind === "new") {
                const made = await createClass(trimmed);
                // Switched to immediately: somebody who just made a class means to use it, and
                // leaving them on the old one puts the next thing they do in the wrong place.
                choose(made.classId);
              } else if (editing.kind === "rename") {
                await renameClass(editing.classId, trimmed);
              }
            });
          }}
        >
          <h2 className="text-title">
            {editing.kind === "new"
              ? COPY.teacher.classes.addTitle
              : COPY.teacher.classes.renameTitle}
          </h2>
          <label className="flex flex-col gap-1 text-caption text-muted">
            {COPY.teacher.classes.name}
            <input
              className={FIELD}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={COPY.teacher.classes.namePlaceholder}
              autoFocus
            />
          </label>
          {error && <p className="text-small text-wrong">{error}</p>}
          <button type="submit" disabled={!name.trim() || busy} className={PRIMARY}>
            {busy ? COPY.enter.working : COPY.teacher.classes.save}
          </button>
          <button type="button" onClick={() => setEditing({ kind: "none" })} className={QUIET}>
            {COPY.teacher.classes.cancel}
          </button>
        </form>
      )}

      {editing.kind === "close" && (
        <div className={`${CARD} px-md py-md`}>
          <h2 className="text-title">{COPY.teacher.classes.closeTitle}</h2>
          {/* What it does and does not do, in full, before it is offered. */}
          <p className="mt-sm text-small">{COPY.teacher.classes.closeWarning}</p>
          {error && <p className="mt-sm text-small text-wrong">{error}</p>}
          <button
            disabled={busy}
            onClick={() =>
              void act(async () => {
                await closeClass(editing.classId);
                // Nothing is chosen here: the provider falls back to the first remaining class,
                // which is the only one certain to still exist afterwards.
              })
            }
            className="mt-md min-h-touch w-full rounded-pill bg-watermelon px-lg py-md text-heading text-card disabled:opacity-60"
          >
            {busy ? COPY.enter.working : COPY.teacher.classes.closeConfirm}
          </button>
          <button onClick={() => setEditing({ kind: "none" })} className={QUIET}>
            {COPY.teacher.classes.cancel}
          </button>
        </div>
      )}
    </div>
  );
}

function Tick() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="3.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12.5 10 17.5 19 7" />
    </svg>
  );
}
